import { useEffect, useRef } from "react";
import { useAppDispatch } from "store/hooks";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "services/firebaseService/firebaseConfig";
import { setNewEventMember } from "../redux/slice";

const EVENT_MEMBER_REF = "EventMember";

export interface IEventMemberFirestoreDoc {
  id: string;
  members: string[];
}
export type EventMemberSnapshotListener = (
  data: IEventMemberFirestoreDoc[] | undefined,
) => Promise<void>;

const useAddEventMembersForEventIds = () => {
  const dispatch = useAppDispatch();

  const onSnapshotHandler = useRef<EventMemberSnapshotListener>(() =>
    Promise.resolve(),
  );
  const listener = useRef<(() => void) | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useEffect(() => unsubscribeListener, []);

  const snapshotListener = async (
    data: IEventMemberFirestoreDoc[] | undefined,
  ) => {
    if (!data?.filter(Boolean)?.length) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await manageEventMemberCache(); // reset and unsubscribe if passed empty id

      return;
    }
    data.forEach((member) =>
      dispatch(setNewEventMember({ [member.id]: member.members })),
    );
  };

  onSnapshotHandler.current = snapshotListener;

  const unsubscribeListener = () => {
    if (listener.current) {
      listener.current();
      listener.current = null;
    }
  };

  const subscribeToFirestore = (eventIds: string[]) => {
    unsubscribeListener();
    listener.current = onSnapshot(
      query(
        collection(firestore, EVENT_MEMBER_REF),
        where("id", "in", eventIds),
      ),
      (snapshot) => {
        onSnapshotHandler.current(
          snapshot.docs.map((change) => change.data()) as
            | IEventMemberFirestoreDoc[]
            | undefined,
        );
      },
    );
  };

  const manageEventMemberCache = async (eventIds?: string[]) => {
    if (eventIds?.length) {
      subscribeToFirestore(eventIds);
    } else {
      unsubscribeListener();
    }
  };

  return {
    subscribeToFirestore,
    unsubscribeListener,
    manageEventMemberCache,
    listener,
  };
};

export default useAddEventMembersForEventIds;
